import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useRegistration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const registerUser = async ({
    email,
    password,
    fullName,
    companyName,
    selectedBusinessType,
    selectedCurrency,
  }: {
    email: string;
    password: string;
    fullName: string;
    companyName: string;
    selectedBusinessType: string;
    selectedCurrency: string;
  }) => {
    setIsLoading(true);

    try {
      console.log("Starting registration process...");
      console.log("Registration data:", {
        email,
        fullName,
        companyName,
        selectedBusinessType,
        selectedCurrency
      });

      if (!selectedBusinessType) {
        throw new Error("Veuillez sélectionner un type d'entreprise");
      }

      // 1. Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            user_type: "owner",
            full_name: fullName,
            company_name: companyName,
            business_type_id: selectedBusinessType,
          },
        },
      });

      if (authError) {
        console.error("Auth error:", authError);
        if (authError.message.includes("User already registered")) {
          throw new Error("Un compte existe déjà avec cet email. Veuillez vous connecter ou utiliser une autre adresse email.");
        }
        throw authError;
      }

      console.log("Auth successful:", authData);

      if (!authData.user) {
        throw new Error("No user data returned after signup");
      }

      // 2. Wait a moment for the trigger to create the profile
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 3. Check if owner_data was created by the trigger
      const { data: existingOwnerData, error: checkError } = await supabase
        .from('owner_data')
        .select('*')
        .eq('owner_id', authData.user.id)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking owner data:", checkError);
        throw checkError;
      }

      // 4. Update owner_data if it exists, otherwise create it
      if (existingOwnerData) {
        const { error: updateError } = await supabase
          .from('owner_data')
          .update({
            company_name: companyName,
            business_type_id: selectedBusinessType,
            currency: selectedCurrency
          })
          .eq('owner_id', authData.user.id);

        if (updateError) {
          console.error("Error updating owner data:", updateError);
          throw updateError;
        }
      } else {
        const { error: insertError } = await supabase
          .from('owner_data')
          .insert({
            owner_id: authData.user.id,
            company_name: companyName,
            business_type_id: selectedBusinessType,
            currency: selectedCurrency
          });

        if (insertError) {
          console.error("Error creating owner data:", insertError);
          throw insertError;
        }
      }

      console.log("Registration complete!");
      toast.success("Compte créé avec succès! Veuillez vérifier votre email pour confirmer votre compte.");
      navigate(`/business/${selectedBusinessType}`);
    } catch (error: any) {
      console.error("Registration error:", error);
      if (error.message.includes("User already registered") || error.message.includes("existe déjà")) {
        toast.error("Un compte existe déjà avec cet email. Veuillez vous connecter ou utiliser une autre adresse email.");
      } else {
        toast.error(`Erreur lors de l'inscription: ${error.message}`);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { registerUser, isLoading };
};